import {getCountryCode} from '@/helpers/shadcn/utils'
import {Constants} from '@/utils/constants/commonConstants'
import {useFeatureValue} from '@growthbook/growthbook-react'
import Papa from 'papaparse'
import {useCallback} from 'react'
import {growthBookFeatureFlags} from '../utils/constants/growthBookFeatureFlags'
import {
  createProductValidator,
  DEFAULT_PRODUCTS_CSV_RULE,
  DEFAULT_SUGGESTED_SKU_CSV_RULE,
  getInventoryCsvRules,
} from '../utils/csvUtils'

const isBlankCsvRow = row =>
  Object.values(row).every(
    value =>
      value === undefined ||
      value === null ||
      value === '' ||
      (typeof value === 'string' && (value.trim() === '' || value.trim().toLowerCase() === 'null'))
  )

/**
 * Custom hook for CSV file validation
 * @returns {Object} Object containing validation functions
 * @returns {Function} validateCSVFile - Validates CSV data against provided rules
 * @returns {Function} validateCSV - Parses and validates a CSV file
 */
export const useCSVFileValidation = () => {
  const inventoryCsvRules = useFeatureValue(growthBookFeatureFlags.InventoryCsvValidationRules)
  const productsCsvRules = useFeatureValue(growthBookFeatureFlags.productsCsvValidationRules)
  const expectedCsvHeadersByCountry =
    useFeatureValue(growthBookFeatureFlags.ProductEnrichementCsvHeadersvalidation, {}) || {}

  const validateCSVHeaders = useCallback((expectedHeaders = [], csvHeaders = []) => {
    const errors = []

    if (!expectedHeaders.length) {
      return errors
    }

    const normalizedHeaders = new Set(
      csvHeaders
        .filter(header => typeof header === 'string')
        .map(header => header.trim())
        .filter(Boolean)
    )

    const missingRequiredHeaders = expectedHeaders
      .filter(Boolean)
      .map(header => header.trim())
      .filter(Boolean)
      .filter(headerName => !normalizedHeaders.has(headerName))

    if (missingRequiredHeaders.length) {
      errors.push(`Missing required column headers: ${missingRequiredHeaders.join(', ')}`)
    }

    return errors
  }, [])

  /**
   * Validates CSV data against provided validation rules
   * @param {Object} fileValidationRules - Validation rules configuration
   * @param {string} fileValidationRules.type - Expected file type (e.g., 'text/csv')
   * @param {string} fileValidationRules.size - Maximum file size in KB
   * @param {Array} fileValidationRules.columns - Column validation rules
   * @param {Object} fileObj - File object containing data to validate
   * @param {string} fileObj.fileType - Actual file MIME type
   * @param {number} fileObj.fileSize - File size in KB
   * @param {Array} fileObj.csvData - Parsed CSV data as array of objects
   * @param {string} uploadFor - Upload context ('product', 'inventory', etc.)
   * @returns {Array<string>} Array of validation error messages
   */
  const validateCSVFile = useCallback((fileValidationRules, fileObj, uploadFor) => {
    const errors = []

    const {type, size, columns = []} = fileValidationRules || {}
    const {fileType, csvData, fileSize, csvHeaders} = fileObj
    const expectedSize = Number(size)
    const availableHeaders = Array.isArray(csvHeaders)
      ? new Set(
          csvHeaders
            .filter(header => typeof header === 'string')
            .map(header => header.trim())
            .filter(Boolean)
        )
      : null

    if (type && type !== fileType) {
      errors.push(`file type should be ${type}`)
    }
    if (expectedSize && fileSize > expectedSize) {
      errors.push(`file size should be less than or equal to ${expectedSize}KB`)
    }

    const validateProduct = uploadFor === 'product' ? createProductValidator() : null

    csvData?.forEach((row, rowIndex) => {
      if (isBlankCsvRow(row)) return

      if (uploadFor === 'product') {
        validateProduct(row, rowIndex, errors)
      }

      if (!columns?.length) return
      columns.forEach(column => {
        const columnName = column?.name

        if (availableHeaders && !availableHeaders.has(columnName)) {
          return
        }

        const columnValue = row[columnName]

        const isEmpty =
          columnValue === undefined ||
          columnValue === null ||
          columnValue === '' ||
          (typeof columnValue === 'string' && columnValue.toLowerCase() === 'null')

        if (column.required && isEmpty) {
          errors.push(`Row ${rowIndex + 1}: '${columnName}' is required.`)
          return
        }

        if (isEmpty) return

        switch (column.type) {
          case 'string':
            if (typeof columnValue !== 'string') {
              errors.push(`Row ${rowIndex + 1}: '${columnName}' should be a string.`)
              return
            }
            if (
              (column.minChar && columnValue.length < column.minChar) ||
              (column.maxChar && columnValue.length > column.maxChar)
            ) {
              errors.push(
                `Row ${rowIndex + 1}: '${columnName}' should have between ${column.minChar} and ${column.maxChar} characters.`
              )
            }
            break

          case 'integer': {
            const intVal = Number(columnValue)
            if (isNaN(intVal) || !Number.isInteger(intVal)) {
              errors.push(`Row ${rowIndex + 1}: '${columnName}' should be an integer.`)
            }
            break
          }

          case 'boolean': {
            const boolVal = typeof columnValue === 'string' ? columnValue.toLowerCase() : columnValue
            if (![true, false, 'true', 'false', 'yes', 'no'].includes(boolVal)) {
              errors.push(`Row ${rowIndex + 1}: '${columnName}' should be a boolean (Yes/No).`)
            }
            break
          }

          case 'boolean_auto': {
            const boolVal = typeof columnValue === 'string' ? columnValue.toLowerCase() : columnValue
            if (!['yes', 'no', 'auto'].includes(boolVal)) {
              errors.push(`Row ${rowIndex + 1}: '${columnName}' should be a boolean (Yes/No/Auto).`)
            }
            break
          }

          case 'float': {
            const floatVal = Number(columnValue)
            if (isNaN(floatVal) || Number.isInteger(floatVal)) {
              errors.push(`Row ${rowIndex + 1}: '${columnName}' should be a floating-point number.`)
            }
            break
          }

          case 'double': {
            const doubleVal = Number(columnValue)
            if (isNaN(doubleVal)) {
              errors.push(`Row ${rowIndex + 1}: '${columnName}' should be a valid double (number).`)
            } else {
              const decimalPlaces = columnValue.toString().split('.')[1]?.length || 0
              if (decimalPlaces < 7) {
                errors.push(
                  `Row ${rowIndex + 1}: '${columnName}' should be a double with higher precision (at least 7 decimal places).`
                )
              }
            }
            break
          }
          case 'number': {
            const numVal = Number(columnValue)
            if (isNaN(numVal)) {
              errors.push(`Row ${rowIndex + 1}: '${columnName}' should be a number (integer or float).`)
            }
            break
          }

          default:
            break
        }
      })
    })

    return errors
  }, [])

  /**
   * Parses a CSV file and validates its content
   * @param {File} file - The CSV file to parse and validate
   * @param {string} uploadFor - Upload context ('product', 'inventory', etc.)
   * @param {Function} onComplete - Callback function invoked after validation
   * @param {Object} onComplete.result - Validation result object
   * @param {Array} onComplete.result.data - Parsed CSV data
   * @param {Array<string>} onComplete.result.errors - Validation errors if any
   */
  const validateCSV = useCallback(
    (file, uploadFor, onComplete) => {
      const countryCode = getCountryCode()
      const validationRuleGetters = {
        inventory: getInventoryCsvRules(inventoryCsvRules, countryCode),
        product: productsCsvRules,
        [Constants.uploadSuggestedSKUs]: DEFAULT_SUGGESTED_SKU_CSV_RULE,
      }

      const validationRules = validationRuleGetters[uploadFor] || DEFAULT_PRODUCTS_CSV_RULE

      Papa.parse(file, {
        skipEmptyLines: true,
        complete: async ({data, meta}) => {
          const parsedHeaders = meta?.fields || []
          const expectedHeaders = uploadFor === 'product' ? expectedCsvHeadersByCountry?.[countryCode] || [] : []
          const validationErrors = [
            ...validateCSVHeaders(expectedHeaders, parsedHeaders),
            ...(data?.some(row => !isBlankCsvRow(row)) ? [] : ['CSV must contain at least one data row.']),
            ...validateCSVFile(
              validationRules,
              {
                fileType: file?.type,
                fileSize: file?.size / 1024,
                csvData: data,
                csvHeaders: parsedHeaders,
              },
              uploadFor
            ),
          ]
          onComplete?.({data, errors: validationErrors})
        },
        header: true,
      })
    },
    [validateCSVFile, validateCSVHeaders, inventoryCsvRules, productsCsvRules, expectedCsvHeadersByCountry]
  )

  return {validateCSVFile, validateCSVHeaders, validateCSV}
}
